// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/agentStepsHelpers.test.ts
import { type AskPunttAgentStep } from "@mg/schemas/src/christo/askPuntt";

// why: extracted out of AgentSteps.tsx (review #5399) -- that component was
// already far past the 400-line ceiling `.claude/rules/anti-ai-slop.md` sets,
// and these four are pure functions over a step. Calling them directly is what
// makes the pairing and memory-summary rules falsifiable: before this, a
// `stepId` mismatch or a dropped prefix could only be provoked by building a
// whole stream fixture and reading the rendered chips back.

/**
 * Does this adjacent `tool_result` actually close THIS `tool_call`?
 *
 * why: rembrandt merges every result into the slot it pre-announced for that
 * tool_use block, matching on `stepId` (see `ask_puntt/chat.py`) — a result is
 * never appended next to the call it belongs to. So for a PARALLEL fan-out of
 * the SAME tool, `[call A pending, call B pending, result C done]` is a normal
 * mid-stream array, and closing B with C's outcome swallowed C's row, flipped
 * still-running B green under C's summary, and under-counted the fan-out.
 * Tool-name adjacency alone could not see that. Steps without a `stepId`
 * (legacy threads, demo/mock streams) keep the old adjacency rule.
 */
export function isResultForCall(
  call: AskPunttAgentStep & { type: "tool_call" },
  result: AskPunttAgentStep & { type: "tool_result" },
): boolean {
  if (result.tool !== call.tool) return false;
  if (call.stepId && result.stepId) return call.stepId === result.stepId;
  return true;
}

/**
 * The write tool's own result line: `Saved to enterprise memory: "<content>"`
 * (or `Corrected …`), followed by the type/scope and Memory ID lines.
 */
export const MEMORY_SUMMARY_PREFIX =
  /^(?:saved to|corrected) enterprise memory:\s*/i;

/**
 * What was remembered, recovered from the RESULT summary.
 *
 * why: rembrandt converts a `tool_call` into a `tool_result` in the SAME array
 * slot and drops `args` on the way (the tool_result schema carries none), so a
 * settled write — and every write in a reloaded thread — reaches Carrie with
 * the statement only in its summary. Reading the call args alone rendered the
 * chip titled "Added a Memory" with nothing remembered under it, which is the
 * generic-pill failure this chip exists to prevent.
 */
export function memoryStatementFromSummary(
  summary: string | undefined,
): string {
  const firstLine = (summary ?? "").split("\n")[0].trim();
  const prefix = MEMORY_SUMMARY_PREFIX.exec(firstLine);
  if (!prefix) return "";
  return firstLine
    .slice(prefix[0].length)
    .trim()
    .replace(/^"/, "")
    .replace(/"$/, "")
    .trim();
}

export function isMemoryCorrectionSummary(
  summary: string | undefined,
): boolean {
  return /^corrected enterprise memory:/i.test(
    (summary ?? "").split("\n")[0].trim(),
  );
}
